import * as React from 'react';
import {Flag, Modal} from "semantic-ui-react";
import ModalTextArea from "./ModalTextArea";
import {IAiMeta, IDataTwoLanguageInput, IVirtualizedTableCellComponentProps} from "../../VirtualizedTableInterfaces";
import {CSSProperties} from "react";
import ReactQuill, {Quill} from 'react-quill';
import {highlightHtml, htmlToNormalizedPlainText, normalizeHtml, plainTextToQuillHtml} from './EditorUtil';
import PromtFormatter from '../reactQuill/PromtFormatter';
import HATEOASClient from 'src/services/HATEOASClient';
import {ResourceListOfOptionDto} from 'src/services/Client';

interface TwoLanguageTextAreaProps extends IVirtualizedTableCellComponentProps {
    isIndicator?: boolean;
    globalSearch?: string;
    isNewGrid?: boolean;
    isFromBaseOptions?: boolean;
    client?: HATEOASClient;
    postOptions?: ResourceListOfOptionDto;
    aiGeneratedLongtext?: boolean;
    aiGeneratedShorttext?:boolean;
    pslAiGeneratedShorttext?:boolean;
    pslAiGeneratedLongtext?:boolean;
    aiGeneratedUsp?:boolean;
    aiGeneratedFooter?:boolean;
    longTextSuggestion?: boolean;
    objectId?:number;
    apiMethod?: (client: HATEOASClient, postOptions: ResourceListOfOptionDto) => {execute: (payload: any) => Promise<any>};
    grammarApiMethod?: (client: HATEOASClient, postOptions: ResourceListOfOptionDto) => {execute: (payload: any) => Promise<any>};
    suggestionApiMethod?: (client: HATEOASClient, postOptions: ResourceListOfOptionDto) => {execute: (payload: any) => Promise<any>};
}

interface TwoLanguageTextAreaState {
    value: IDataTwoLanguageInput | null;
    keyLanguage: string;
    de: number;
    gb: number;
    hideTextarea: boolean;
    closeOnDimmerClick: boolean;
}
export default class TwoLanguageTextArea extends React.PureComponent<TwoLanguageTextAreaProps, TwoLanguageTextAreaState> {
    private quillRefs: {[key: string]: ReactQuill | null} = {};
    constructor(props: TwoLanguageTextAreaProps) {
        super(props);
        this.state = {
            value: null,
            keyLanguage: 'de',
            de: 0,
            gb: 0,
            hideTextarea: false,
            closeOnDimmerClick: true
        };
        this.onValueChange = this.onValueChange.bind(this);
        this.saveChange = this.saveChange.bind(this);
        this.onHeightChange = this.onHeightChange.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onOpen = this.onOpen.bind(this);
        this.handleKeyLanguageChange = this.handleKeyLanguageChange.bind(this);
        this.renderModalTrigger = this.renderModalTrigger.bind(this);
    }

    componentDidMount() {
        const value: IDataTwoLanguageInput = this.props.value;
        this.setState({value: value});
        this.addClipboardMatchers();
    }

    componentDidUpdate(prevProps: TwoLanguageTextAreaProps) {
        const propsValue = this.props.value || "";
        const prevPropsValue = prevProps.value || "";
        if (propsValue !== prevPropsValue && this.state.value !== propsValue) {
            this.setState({value: propsValue, hideTextarea: false});
        }
        this.addClipboardMatchers();
    }

    private addClipboardMatchers() {
        Object.keys(this.state.value || {}).forEach(key => {
            const ref = this.quillRefs[key];
            if (ref && typeof (ref as any).getEditor === 'function') {
                const editor = (ref as any).getEditor();
                // Only add once
                if (!editor.__mediaBlockerAdded) {
                    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
                        if (
                            node.nodeName === "IMG" ||
                            node.nodeName === "AUDIO" ||
                            node.nodeName === "VIDEO"
                        ) {
                            return new (Quill.import("delta"))();
                        }
                        return delta;
                    });
                    editor.clipboard.addMatcher("IMG", () => {
                        return new (Quill.import("delta"))();
                    });
                    editor.__mediaBlockerAdded = true;
                }
            }
        });
    }

    private onValueChange = (key: string, value: string, aiMeta?: IAiMeta) => {
        const currentValue = this.state.value;
        if (!currentValue) return;
        const targetItem = { ...currentValue[key] };
        targetItem.value = value.replace(/(\r\n|\n|\r)/gm, "");

        if (aiMeta !== undefined) {
            targetItem.aiMeta = aiMeta;
        }

        this.setState({
            value: {
                ...currentValue,
                [key]: targetItem
            }
        });
    }

    private saveChange() {
        const newValue: IDataTwoLanguageInput = this.state.value as IDataTwoLanguageInput;
        const oldValue: IDataTwoLanguageInput = this.props.value as IDataTwoLanguageInput;

        for (const key of Object.keys(oldValue)) {
            const oldRaw = oldValue[key].value || '';
            const newRaw = newValue[key].value || '';
            // Normalize both to plain text for comparison
            const oldPlain = htmlToNormalizedPlainText(oldRaw);
            const newPlain = htmlToNormalizedPlainText(newRaw);

            const oldHtml = /^<.*?>.*<\/.*?>$/.test(oldRaw) ? normalizeHtml(oldRaw) : normalizeHtml(plainTextToQuillHtml(oldRaw));
            const newHtml = normalizeHtml(newRaw);

            // Save if text or formatting changed
            if (oldPlain !== newPlain || oldHtml !== newHtml) {
                this.props.onChange(newValue);
                this.setState({hideTextarea: true});
                break;
            }
        }
    }

    private onHeightChange(key: string, height: number) {
        // @ts-ignore already checked in render
        if (this.state.value[key].value === this.props.value[key].value) {
            // @ts-ignore key can only be 'gb' or 'de'
            this.setState({[key]: height});
        }
    }

    private onClose() {
        this.saveChange();
        if (this.props.clearActiveRow) this.props.clearActiveRow();
    }

    private onOpen() {
        if (this.props.setActiveRow) this.props.setActiveRow();
        // Blur all grid ReactQuill editors
        Object.keys(this.quillRefs).map(key => this.quillRefs[key]).forEach(ref => {
            if (ref && typeof (ref as any).getEditor === 'function') {
                const editor = (ref as any).getEditor();
                if (editor && editor.root) {
                    editor.root.blur();
                }
            }
        });
    }
    private handleKeyLanguageChange(key: string) {
        this.setState({keyLanguage: key});
    }

    private renderModalTrigger() {
        const valueObj = this.state.value;
        // @ts-ignore
        if (!valueObj) return null;
        const keys = Object.keys(valueObj);
        const editingColumn = this.props.editDialogHeader ? this.props.editDialogHeader.editingColumn : '';

        const getIsAIGenerated = (langItem: any) => {
            const localMeta = langItem.aiMeta;
            switch (editingColumn) {
                case 'Shorttext':
                case 'Kurztext':
                    return Boolean(this.props.pslAiGeneratedShorttext || (localMeta && localMeta.aiGeneratedShorttext));
                case 'Longtext':
                case 'Langtext':
                    return Boolean(this.props.pslAiGeneratedLongtext || this.props.aiGeneratedLongtext || (localMeta && localMeta.aiGeneratedLongtext));
                case 'Footer':
                    return Boolean(this.props.aiGeneratedFooter || (localMeta && localMeta.aiGeneratedFooter));
                case 'USP':
                    return Boolean(this.props.aiGeneratedUsp || (localMeta && localMeta.aiGeneratedUsp));
                default:
                    return false;
            }
        };

        const cellHasAIGeneratedContent = keys.some((key) => {
        // @ts-ignore
            return getIsAIGenerated(valueObj[key]);
        });

        return (
            <div className={cellHasAIGeneratedContent ? "ai-illuminated-border" : ""} style={{display: 'flex', flexDirection: 'column'}}>
                {keys.map((key) => {
                    // @ts-ignore
                    const langData = valueObj[key];
                    const flagName = langData.flag;
                    // @ts-ignore
                    const quillValue = langData.value || '';
                    return (
                        <div
                            key={key}
                            className={`editor-parent ${this.props.readOnly ? 'disabled' : ''}`}
                            onClick={() => this.handleKeyLanguageChange(key)}
                        >
                            {/* Flag Component */}
                            <div style={{display: 'flex', alignItems: 'center', padding: '0 5px'}}>
                                <Flag name={flagName} />
                            </div>
                            {/* Vertical Separator */}
                            <div className="vertical-separator" />
                            {/* ReactQuill Component */}
                            <ReactQuill
                                ref={ref => {this.quillRefs[key] = ref;}}
                                className="custome-quill"
                                modules={{toolbar: false}} // Hide toolbar
                                readOnly={true} // Make ReactQuill read-only
                                value={
                                    (() => {
                                        let val = !/^<.*?>.*<\/.*?>$/.test(quillValue)
                                            ? plainTextToQuillHtml(quillValue)
                                            : quillValue || "";
                                        if (this.props.globalSearch && val) {
                                            val = highlightHtml(val, this.props.globalSearch);
                                        }
                                        return val;
                                    })()
                                }
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    public render() {
        if (!this.state.value)
            return null;

        if (this.props.uglyMode && !this.state.hideTextarea) {
            const editingColumn = this.props.editDialogHeader ? this.props.editDialogHeader.editingColumn : '';
            if (editingColumn && (editingColumn === 'Footer' || editingColumn === 'USP' || this.props.isFromBaseOptions)) {
                return (
                    <div className="display--flex">
                        <div style={{width: '-webkit-fill-available'}}>
                            {
                                Object.keys(this.state.value).map(key => (
                                    <PromtFormatter
                                        className='with-border-quill-custome'
                                        value={
                                            this.state.value &&
                                                this.state.value[key]
                                                ? this.state.value[key].value || ""
                                                : ""}
                                        readOnly={true}
                                    />

                                ))
                            }
                        </div>
                    </div>
                );
            }
            return Object.keys(this.state.value).map(key => (
                <PromtFormatter
                    className='with-border-quill-custome'
                    value={
                        this.state.value &&
                            this.state.value[key]
                            ? this.state.value[key].value || ""
                            : ""}
                    readOnly={true}
                />

            ));
        }

        return (
            <Modal
                onClose={this.onClose}
                onOpen={this.onOpen}
                closeOnDimmerClick={this.state.closeOnDimmerClick}
                dimmer={'inverted'}
                style={{height: '57%'}}
                trigger={this.renderModalTrigger()}
            >
                <ModalTextArea
                    value={this.state.value}
                    readOnly={this.props.readOnly}
                    focusKey={this.state.keyLanguage}
                    onChange={this.onValueChange}
                    dialogHeader={this.props.editDialogHeader}
                    longTextSuggestion={this.props.longTextSuggestion}
                    objectId={this.props.objectId}
                    closeOnDimmerClick={(value) =>
                        this.state.closeOnDimmerClick !== value && this.setState({closeOnDimmerClick: value})
                    }
                    isEditor={true}
                    isNewGrid={this.props.isNewGrid ? true : false}
                    client={this.props.client}
                    postOptions={this.props.postOptions}
                    apiMethod={this.props.apiMethod}
                    grammarApiMethod={this.props.grammarApiMethod}
                    suggestionApiMethod={this.props.suggestionApiMethod}
                />
            </Modal >
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Table/components/twoLanguage/TwoLanguageTextArea.tsx